import {removeFromArray} from '../../../../src/utils/array.ts';
import {div} from '../../../../src/utils/html.ts';
import type {CardLayout, NodeChunk} from '../../../../types/types.ts';
import {createElement, shouldNotBeOrphan} from '../../../lib/dom.ts';
import type {Article} from '../../../models/article.ts';

export function shallowCloneChunk(chunk: NodeChunk): NodeChunk {
	return {
		figures: [...chunk.figures],
		nodes: [...chunk.nodes],
		extraNodes: [...chunk.extraNodes],
		chars: chunk.chars,
		layout: chunk.layout
	};
}

export function splitNodes(nodes: HTMLElement[], maxChars: number) {
	let chars = 0;
	const indexToSplitAt = nodes.findIndex((node) => {
		if (chars + node.innerText.length > maxChars) {
			return true;
		}
		chars += node.innerText.length;
		return false;
	});

	// If we can't at least move one node, abort
	if (indexToSplitAt < 1) {
		return [];
	}

	return nodes.splice(indexToSplitAt);
}

export function splitChunk(chunk: NodeChunk, maxChars: number) {
	const extraNodes = splitNodes(chunk.nodes, maxChars);

	if (!extraNodes.length) {
		return null;
	}

	return createChunk(extraNodes);
}

export function createChunk(nodes: HTMLElement[] = [], figures: HTMLElement[] = [], layout = '') {
	return {
		nodes,
		extraNodes: [[]],
		figures,
		chars: getNodesCharCount(nodes),
		layout
	} as NodeChunk;
}

function getNodesCharCount(nodes: HTMLElement[]) {
	// TODO: Account for empty lines between nodes
	return nodes.reduce((acc, node) => acc + node.innerText.length, 0);
}

export function splitBodyTextAndFigures(markup: string, document: Document, isArticleGroup: boolean) {
	const tempEl = createElement(document, 'div') as HTMLElement;
	tempEl.innerHTML = markup;
	if (isArticleGroup) {
		return handleArticleGroup();
	}
	return handleArticle();

	function handleArticle() {
		const bodyClassesToSkip = [
			'headline',
			'headline--ingresstittel',
			'intro',
			'kicker',
			'quote',
			'byline',
			'byline-image',
			'byline-multi',
			'readmore',
			'dn-relation-block'
		];
		const elements = Array.from(tempEl.children)
			.flatMap((childEl) => Array.from(childEl.children))
			.flatMap((childEl) => {
				// Some layouts wrap content in divs with data-content-order="<number>"
				// Consider whether we need to sort by this field as well.
				if (childEl instanceof HTMLElement && childEl.dataset.contentOrder) {
					return Array.from(childEl.children);
				}
				return childEl;
			});
		// Array.from(tempEl.firstElementChild!.children);
		if (!elements.length) {
			return [[], []];
		}

		const figures: HTMLElement[] = [];
		const captions: HTMLElement[] = [];
		const pictures: HTMLElement[] = [];
		const infographics: HTMLElement[] = [];
		const videos: HTMLElement[] = [];
		const others: HTMLElement[] = [];
		const bodyTextNodes: HTMLElement[] = [];

		let hasPageTop = false;

		for (const el of elements) {
			if (!(el instanceof HTMLElement)) {
				continue;
			}

			if (el instanceof HTMLUListElement || el instanceof HTMLOListElement) {
				const firstListElement = el.firstElementChild as HTMLLIElement;
				if (!firstListElement?.innerText) {
					// Assume this a continuation list, which we skip
					console.log('Skipped list with empty first element');
					continue;
				}
			}

			if (el.dataset.component === 'topic') {
				if (hasPageTop) {
					continue;
				}

				others.unshift(el);
				hasPageTop = true;
				continue;
			}

			if (el.querySelector('amedia-embed')) {
				// TODO: Some amedia-embeds should be rendered
				continue;
			}

			if (el.classList.contains('page-top')) {
				if (hasPageTop) {
					continue;
				}
				hasPageTop = true;
			}

			if (el.classList.contains('caption')) {
				captions.push(el);
				continue;
			}

			if (el.classList.contains('picture') && !el.classList.contains('picture--inline')) {
				pictures.push(el);
				continue;
			}

			if (el.classList.contains('fact-box')) {
				if (el.innerText.length > 600) {
					el.classList.add('fact-box--large');
				}
			}

			if (el.classList.contains('infographic')) {
				if (el.querySelector('source[srcset*="QRkode"]')) {
					continue;
				}
				infographics.push(el);
				continue;
			}

			if (el.classList.contains('dp-plugin-youtube-search')) {
				videos.push(el);
				el.classList.add('figure--video');
				continue;
			}

			others.push(el);
		}

		pictures.sort(
			(a, b) =>
				Number(a.dataset.index ?? Number.POSITIVE_INFINITY) -
				Number(b.dataset.index ?? Number.POSITIVE_INFINITY)
		);
		captions.sort(
			(a, b) =>
				Number(a.dataset.index ?? Number.POSITIVE_INFINITY) -
				Number(b.dataset.index ?? Number.POSITIVE_INFINITY)
		);

		for (const el of pictures) {
			// if (el.dataset.index === '1' || el.dataset.linked) {
			if (el.dataset.linked) {
				continue;
			}

			const figcaptionEl = el.querySelector('figcaption');
			if (figcaptionEl?.innerText.trim() === '') {
				figcaptionEl.remove();
			}

			if (!figcaptionEl) {
				if (el.dataset.guid) {
					const caption = captions.find((captionEl) => captionEl.dataset.guid === el.dataset.guid);
					if (caption) {
						caption.dataset.linked = 'true';
						el.appendChild(caption);
						removeFromArray(captions, caption);
					}
				} else {
					// TODO: This may pull a caption with a guid, which should have been matched later
					const caption = captions.shift();
					if (caption instanceof HTMLElement) {
						caption.dataset.linked = 'true';
						el.appendChild(caption);
					}
				}
			}

			formatPicture(el);
			figures.push(el);
		}

		// TODO: Handle infopgraphics, they must be rendered without cropping, and usually in a single column
		for (const el of infographics) {
			figures.push(el);
		}

		for (const el of videos) {
			figures.push(el);
		}

		for (const el of others) {
			if (!(el instanceof HTMLElement)) {
				continue;
			}

			if (el instanceof HTMLSpanElement && el.innerText.trim() === '') {
				continue;
			}

			if (el instanceof HTMLDivElement && bodyClassesToSkip.some((value) => el.classList.contains(value))) {
				continue;
			}

			bodyTextNodes.push(el);
		}

		figures.forEach((figureEl, index) => {
			figureEl.classList.add('figure');
			figureEl.classList.add(`figure-${index + 1}`);
			const imageEl = figureEl.querySelector('img');
			if (imageEl) {
				const imageWrapperEl = figureEl.querySelector('.image-wrapper') as HTMLDivElement;
				if (imageWrapperEl) {
					imageWrapperEl.style.setProperty('--asset-aspect-ratio', `${imageEl.width} / ${imageEl.height}`);
				}
			}
		});

		return [bodyTextNodes, figures];
	}

	function handleArticleGroup() {
		const headerEl = createElement(tempEl.ownerDocument, 'div', 'header--notis-page');
		headerEl.innerText = 'Notiser';
		const elements: HTMLElement[] = [
			headerEl,
			...(Array.from(tempEl.children).flatMap((childEl) => Array.from(childEl.children)) as HTMLElement[])
		];
		for (const el of elements) {
			if (!(el instanceof HTMLElement)) {
				continue;
			}
			if (el instanceof HTMLSpanElement && el.innerText.trim() === '') {
				el.remove();
			}
			if (el.classList.contains('picture')) {
				formatPicture(el);
			}
		}

		return [elements, []];
	}
}

type CharLimits = {
	regular: number;
	firstCardNoImage: number;
	firstCardWithImage: number;
	landscapeFigure: number;
	wideFigure: number;
	tallFigure: number;
	smallSquareFigure: number;
	squareFigure: number;
};

export function splitMobileChunks(
	sourceChunks: NodeChunk[],
	article: Article,
	extraMobileHeight: number,
	layouts: Record<number, CardLayout | undefined>
) {
	const charLimits: CharLimits = {
		regular: 900 * extraMobileHeight,
		// firstCardNoImage: 800,
		firstCardNoImage: (900 - getMobileHeadElementsCharactersEstimate(article)) * extraMobileHeight,
		firstCardWithImage: (500 - getMobileHeadElementsCharactersEstimate(article)) * extraMobileHeight,
		landscapeFigure: 450 * extraMobileHeight,
		wideFigure: 200 * extraMobileHeight,
		tallFigure: 200 * extraMobileHeight,
		smallSquareFigure: 200 * extraMobileHeight,
		squareFigure: 200 * extraMobileHeight
	};

	const chunks = sourceChunks.flatMap((curr, index) => {
		if (index === 1 && !(curr.nodes.length || curr.figures.length)) {
			// Consumed by first chunk
			return [];
		}
		let pendingChunk = curr;
		const chunks = [pendingChunk];

		let maxChars = getMobileMaxChars(pendingChunk, sourceChunks[1], charLimits, index);

		let iterations = 0;
		while (pendingChunk.chars > maxChars && iterations < 10) {
			iterations++;
			if (!pendingChunk.figures.length && index > 0) {
				maxChars = charLimits.regular;
			}
			const newChunk = splitChunk(pendingChunk, maxChars);
			if (!newChunk) {
				break;
			}

			pendingChunk.chars = pendingChunk.chars - newChunk.chars;
			chunks.push(newChunk);
			pendingChunk = newChunk;
		}
		return chunks;
	});

	// TODO: Account for moved cards when layout pushes content
	Object.entries(layouts).forEach(([index, layout]) => {
		const chunk = chunks[Number(index) - 1];
		if (chunk && layout) {
			chunk.layout = layout;
		}
	});

	return chunks;
}

function getMobileMaxChars(chunk: NodeChunk, nextChunk: NodeChunk, charLimits: CharLimits, index: number) {
	if (index === 0) {
		if (chunk.figures.length >= 1) {
			return charLimits.firstCardWithImage;
		}

		// First chunk has no body text nodes, if next chunk has no figures, we move the content the first chunk
		if (nextChunk?.figures.length === 0) {
			chunk.nodes = nextChunk.nodes;
			chunk.chars = nextChunk.chars;
			nextChunk.nodes = [];
		}

		return charLimits.firstCardNoImage;
	}

	if (chunk.figures.length) {
		// TODO: Check figure type
		return charLimits.landscapeFigure;
	}

	return charLimits.regular;
}

export function splitDesktopChunks(
	sourceChunks: NodeChunk[],
	_article: Article,
	layouts: Record<number, CardLayout | undefined>
) {
	const firstCardChunk = sourceChunks[0];
	// Assumes first chunk has no body text nodes
	if (sourceChunks[1]?.figures.length === 0) {
		firstCardChunk.nodes = sourceChunks[1].nodes;
		firstCardChunk.chars = sourceChunks[1].chars;
		sourceChunks.splice(1, 1);
	}

	// Apply layout to first chunk (card 1)
	if (layouts[1]) {
		firstCardChunk.layout = layouts[1];
	}

	const chunks = [firstCardChunk];

	// Pending chunks are created when we need to split a card
	let pendingChunk: NodeChunk | null = null;
	sourceChunks.slice(1).forEach((chunk) => {
		if (pendingChunk) {
			// If the overlfowing content can't fit on the next card, we insert an extra card
			// NOTE: The newly created card may be a bit sparse, but it's expected that the next chunk should start a new card
			if (pendingChunk.chars + chunk.chars > 2000) {
				chunks.push(pendingChunk);
			} else {
				chunk.nodes = [...pendingChunk.nodes, ...chunk.nodes];
				chunk.chars = pendingChunk.chars + chunk.chars;
			}
			pendingChunk = null;
		}

		const layout = layouts[chunks.length + 1];
		if (layout) {
			chunk.layout = layout;
		}
		// When chars exceed 1200, we need a split grid setup
		if (!layout && chunk.figures.length === 1 && chunk.chars > 1200) {
			// If chars exceed 2000, we need a new card to fit all the content
			if (chunk.chars > 2000) {
				pendingChunk = splitChunk(chunk, 2000);
			}
			chunk.extraNodes = [splitNodes(chunk.nodes, 1050)];
			chunk.layout = '2col-image-top-right';
		}
		chunks.push(chunk);
	});

	if (pendingChunk) {
		chunks.push(pendingChunk);
	}

	return chunks;
}

export function splitIntoChunks(
	article: Article,
	bodyTextNodes: HTMLElement[],
	figures: HTMLElement[],
	mobileMode: boolean,
	layouts: Record<number, string | undefined>,
	extraMobileHeight: number
) {
	const charLimitsFull = {
		regular: 4200,
		firstCardWithImage: 2300,
		firstCardNoImage: 4300 - getHeadElementsCharactersEstimate(article),
		landscapeFigure: 2200,
		wideFigure: 1800,
		tallFigure: 2400,
		smallSquareFigure: 3200,
		squareFigure: 2400
	};

	const charLimitsMobile = {
		regular: 900 * extraMobileHeight,
		// firstCardNoImage: 800,
		firstCardNoImage: (900 - getMobileHeadElementsCharactersEstimate(article)) * extraMobileHeight,
		firstCardWithImage: (500 - getMobileHeadElementsCharactersEstimate(article)) * extraMobileHeight,
		landscapeFigure: 450 * extraMobileHeight,
		wideFigure: 200 * extraMobileHeight,
		tallFigure: 200 * extraMobileHeight,
		smallSquareFigure: 200 * extraMobileHeight,
		squareFigure: 200 * extraMobileHeight
	};

	const charLimits = mobileMode ? charLimitsMobile : charLimitsFull;
	const EMPTY_LINE_CHARS = mobileMode ? 50 : 40;

	const deferredNodes: HTMLElement[] = [];
	const deferredFigures: HTMLElement[] = [];
	const preconsumedNodes: HTMLElement[] = [];

	const chunks = bodyTextNodes.reduce(
		(acc: NodeChunk[], curr: HTMLElement) => {
			if (preconsumedNodes.includes(curr)) {
				return acc;
			}
			const isFirstCard = acc.length === 1;
			// Space on non-first card without any figures
			let maxChars = charLimits.regular;
			const currentChunk = acc.at(-1) as NodeChunk;
			const currentNodeChars = curr.innerText.trim().length;

			if (currentChunk.layout === 'commentary' && currentChunk.figures.length) {
				const figure = currentChunk.figures.pop();
				if (figure) {
					figures.unshift(figure);
				}
			}

			if (curr.classList.contains('page-break-before')) {
				if (currentChunk.nodes.length === 0) {
					currentChunk.nodes.push(curr);
					currentChunk.chars = currentNodeChars;
				} else {
					const nextChunk = createChunk([curr]);

					acc.push(nextChunk);
				}

				if (curr.classList.contains('page-break-after')) {
					const nextChunk = createChunk();
					acc.push(nextChunk);
				}

				return acc;
			}

			if (curr.classList.contains('page-break-after')) {
				currentChunk.nodes.push(curr);
				currentChunk.chars + currentNodeChars + EMPTY_LINE_CHARS;
				const nextChunk = createChunk();
				acc.push(nextChunk);
				return acc;
			}

			if (isFirstCard) {
				if (currentChunk.layout && currentChunk.layout !== 'commentary') {
					maxChars = 0;
				} else if (currentChunk.figures.length && currentChunk.layout !== 'commentary') {
					// First card, has picture
					maxChars = charLimits.firstCardWithImage;
				} else if (hasHeadElements(article)) {
					// First card, no picture
					maxChars = charLimits.firstCardNoImage;
				}
			} else {
				const figure = currentChunk.figures[0];

				if (figure) {
					// On mobile, we want tall images to be on their own cards
					if (
						(mobileMode &&
							(figure.classList.contains('picture--portrait') ||
								figure.classList.contains('picture--tall'))) ||
						(figure.classList.contains('picture--landscape') &&
							currentNodeChars > charLimits.landscapeFigure)
					) {
						const nextChunk = createChunk([...currentChunk.nodes.splice(0), curr]);
						acc.push(nextChunk);
						return acc;
					}
					if (figure.classList.contains('picture--landscape')) {
						// Non-first card with picture
						maxChars = charLimits.landscapeFigure;
					} else if (figure.classList.contains('picture--wide')) {
						maxChars = charLimits.wideFigure;
					} else if (
						figure.classList.contains('picture--portrait') ||
						figure.classList.contains('picture--tall')
					) {
						maxChars = charLimits.tallFigure;
					} else if (figure.classList.contains('picture--square')) {
						if (figure.classList.contains('picture--small')) {
							maxChars = charLimits.smallSquareFigure;
						} else {
							maxChars = charLimits.squareFigure;
						}
					}
				}
			}

			if (
				!mobileMode &&
				isFirstCard &&
				currentChunk.figures.length &&
				curr.classList.contains('fact-box--large')
			) {
				deferredNodes.push(curr);
				return acc;
			}

			const nextChunkFigures = [];
			let startNewCard = currentChunk.chars + currentNodeChars > maxChars;
			// TODO: This is an attempt to more logically split cards, but most of this has been moved to contentGroups
			//    in the adapter. This is disabled for now, and should probably be removed.
			if (curr.tagName === 'xH2' || curr.tagName === 'xH3') {
				const nextFigure = findImageBeforeNextHeading(curr);
				startNewCard = nextFigure !== null;
				if (nextFigure && !mobileMode) {
					nextChunkFigures.push(nextFigure as HTMLElement);
					preconsumedNodes.push(nextFigure.closest('amedia-embed') || (nextFigure as HTMLElement));
				}
			}

			// Current element can't fit on card, start new card
			if (startNewCard) {
				const nextChunkNodes: HTMLElement[] = [];
				if (mobileMode) {
					if (deferredFigures.length && currentNodeChars <= charLimits.landscapeFigure) {
						nextChunkFigures.push(deferredFigures.shift());
					} else {
						if (figures.length && acc.length % 3 === 0) {
							if (
								figures[0].classList.contains('picture--landscape') &&
								currentNodeChars > charLimits.landscapeFigure
							) {
								deferredFigures.push(figures.shift() as HTMLElement);
							} else {
								nextChunkFigures.push(figures.shift());
							}
						}
					}
				} else {
					if (figures.length && !deferredNodes.some((node) => node.classList.contains('fact-box--large'))) {
						nextChunkFigures.push(figures.shift());
					}

					let node = deferredNodes.shift();
					while (node) {
						nextChunkNodes.push(node);
						node = deferredNodes.shift();
					}
				}

				const nextChunk = {
					nodes: nextChunkNodes,
					figures: nextChunkFigures,
					chars: getNodesCharCount(nextChunkNodes),
					layout: layouts[acc.length + 1]
				} as NodeChunk;
				let lastElOnPreviousCard = currentChunk.nodes.at(-1);
				acc.push(nextChunk);

				while (lastElOnPreviousCard && shouldNotBeOrphan(lastElOnPreviousCard)) {
					currentChunk.nodes.pop();
					nextChunk.nodes.unshift(lastElOnPreviousCard);
					nextChunk.chars = lastElOnPreviousCard.innerText.length + EMPTY_LINE_CHARS;
					currentChunk.chars = currentChunk.chars - lastElOnPreviousCard.innerText.length + EMPTY_LINE_CHARS;
					lastElOnPreviousCard = currentChunk.nodes.at(-1);
				}
			}

			const lastChunk = acc.at(-1);
			if (!lastChunk) {
				return acc;
			}
			lastChunk.nodes.push(curr);
			lastChunk.chars += currentNodeChars + EMPTY_LINE_CHARS;

			if (curr.classList.contains('picture')) {
				if (curr.dataset.alignment === 'left') {
					lastChunk.chars += 5 * EMPTY_LINE_CHARS;
				} else {
					lastChunk.chars += 10 * EMPTY_LINE_CHARS;
				}
			}

			return acc;
		},
		[createChunk([], figures.length ? [figures.shift() as HTMLElement] : [], layouts[1])]
	);

	while (figures.length) {
		chunks.push(createChunk([], [figures.shift() as HTMLElement], layouts[chunks.length + 1]));
	}

	// This will make sure that single fact boxes are the last entries, while two fact boxes in the same chunk
	// are moved to be the first and last entries.
	// This is likely to reduce white space caused by partial columns.
	chunks.forEach(({nodes}) => {
		const factBoxNodes = nodes.filter((node: HTMLElement) => {
			return node.classList.contains('fact-box');
		});

		if (factBoxNodes.length === 1) {
			nodes.push(...nodes.splice(nodes.indexOf(factBoxNodes[0]), 1));
		}

		if (factBoxNodes.length === 2) {
			nodes.unshift(...nodes.splice(nodes.indexOf(factBoxNodes[0]), 1));
			nodes.push(...nodes.splice(nodes.indexOf(factBoxNodes[1]), 1));
		}
	});

	return chunks;
}

export function findImageBeforeNextHeading(node: HTMLElement) {
	let next = node.nextElementSibling;
	do {
		if (!next || next.tagName === 'H2' || next.tagName === 'H3') {
			return null;
		}

		if (next.classList.contains('figure')) {
			return next;
		}

		if (next.tagName === 'AMEDIA-EMBED' || (next.tagName === 'P' && next.querySelector('amedia-embed img'))) {
			return next.querySelector('.figure, amedia-embed');
		}

		next = next.nextElementSibling;
	} while (next);

	return null;
}

function hasHeadElements(article: Article) {
	return Boolean(article.title || article.pretitle || article.intro || article.byline?.html);
}

function getHeadElementsCharactersEstimate(article: Article) {
	let chars = 0;

	if (article.pretitle) {
		chars += 100;
	}

	if (article.title) {
		chars += 400;
	}

	if (article.intro) {
		chars += article.intro.length <= 100 ? 150 : 300;
	}

	if (article.byline?.html) {
		chars += 400;
	}

	return chars;
}

function getMobileHeadElementsCharactersEstimate(article: Article) {
	const charsPerLine = 35;
	let lines = 0;

	if (article.pretitle) {
		lines++;
	}

	if (article.title) {
		lines += Math.ceil(article.title.length / 30);
	}

	if (article.intro) {
		lines += Math.ceil(article.intro.length / 35);
	}

	if (article.byline?.html) {
		lines += 2;
	}

	return charsPerLine * lines + 1;
}

function formatPicture(el: HTMLElement) {
	// guid means this is an image from LayoutPreview markup
	if (!(el.dataset.guid || el.dataset.component === 'picture' || el.dataset.component === 'picture--main')) {
		return;
	}
	const imgEl = el.querySelector('img');
	const captionEl = el.querySelector('[data-element-type="caption"], span.caption, .caption-wrapper .caption');
	const creditEl = el.querySelector('[data-element-type="credit"], span.credit, .caption-wrapper .credit');
	if (captionEl?.firstElementChild && captionEl.firstElementChild === captionEl.lastElementChild) {
		captionEl.innerHTML = captionEl.firstElementChild.innerHTML;
	}

	const parts = [div(imgEl?.outerHTML, 'image-wrapper')];

	if (captionEl || creditEl) {
		parts.push(
			div(`${div(captionEl?.innerHTML, 'caption')} ${div(creditEl?.innerHTML, 'credit')}`, 'caption-wrapper')
		);
	}

	el.innerHTML = parts.join('\n');
	el.dataset.formatted = '1';
}
